import React from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import gtm from 'general/js/gtm';
import Scroller from 'general/js/scroller';
import eventBus from 'general/js/event-bus';
import ScoringLoader, { endpointNames } from 'general/js/loaders/scoring-loader';
import {
    getRoundById,
    getPlayerSitecoreData,
    isRoundChoicesEqual,
    isRoundChoiceAvailable,
    setSelectedChoice,
} from 'general/js/helpers';
import favoritePlayerService,
{
    EVENT_FAVOURITE_PLAYER_SERVICE_ADD,
    EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE
} from 'general/js/services/favorite-player-services';
import { BlockPlaceholder } from 'components/block-placeholder';
import { BlockSpinner } from 'components/block-spinner';
import { LivePreMessage, bemClasses } from 'components/live-pre-message';
import { getDefaultCallback, getSuggestionList } from 'components/search-autocomplete';
import { PlayerSuggestion, CountrySuggestion } from 'components/search-suggestions';
import TeeTimesWidgetBar from './tee-times-widget-bar';
import TeeTimesWidgetList from './tee-times-widget-list';
import TeeTimesWidgetFooter from './tee-times-widget-footer';
import { groupStatus, nextOutAvailablityRounds } from './enums';

class TeeTimesWidgetLayout extends React.Component {
    constructor(props) {
        super(props);
        this.nextOutNode = null;
        this.state = {
            roundChoices: null,
            selectedRoundId: null,
            hasUserSelected: false,
            showFavourites: false,
            favoritePlayerList: favoritePlayerService.getAll(),
            sortOrder: 'ASC',
            isFetching: false,
            predictiveSearch: [],
            isPredictive: false,
        };
    }

    componentDidMount() {
        ScoringLoader.subscribe(endpointNames.LIVE_SCANNER);
        ScoringLoader.subscribe(endpointNames.PLAYERS);
        ScoringLoader.subscribe(endpointNames.TEE_TIMES);
        eventBus.addListener(EVENT_FAVOURITE_PLAYER_SERVICE_ADD, this.updateFavorites);
        eventBus.addListener(EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE, this.updateFavorites);
        this.updateChoices();
    }

    componentDidUpdate() {
        this.updateChoices();
    }

    componentWillUnmount() {
        ScoringLoader.unsubscribe(endpointNames.LIVE_SCANNER);
        ScoringLoader.unsubscribe(endpointNames.PLAYERS);
        ScoringLoader.unsubscribe(endpointNames.TEE_TIMES);
        eventBus.removeListener(EVENT_FAVOURITE_PLAYER_SERVICE_ADD, this.updateFavorites);
        eventBus.removeListener(EVENT_FAVOURITE_PLAYER_SERVICE_REMOVE, this.updateFavorites);
    }

    getAvailableChoices() {
        const { liveScanner, scoringTeeTimes } = this.props;
        const roundChoices = get(liveScanner, 'liveComponents.teeTimes.roundChoices');

        if (!scoringTeeTimes || !Array.isArray(scoringTeeTimes.rounds)) return [];

        return cloneDeep(roundChoices).filter((choice) => {
            return scoringTeeTimes.rounds.find((round) =>
                round.id.toString() === choice.value.toString());
        });
    }

    updateChoices() {
        const { liveScanner, scoringTeeTimes } = this.props;
        const { selectedRoundId } = this.state;

        if (!liveScanner || !scoringTeeTimes) return;

        const prevChoices = this.state.roundChoices;
        const roundChoices = this.getAvailableChoices();

        if (this.state.hasUserSelected &&
            isRoundChoiceAvailable(roundChoices, selectedRoundId)
        ) {
            setSelectedChoice(roundChoices, selectedRoundId);

            if (!isRoundChoicesEqual(prevChoices, roundChoices)) {
                this.setState({ roundChoices });
            }
            return;
        }

        const foundChoice = roundChoices.find((choice) => choice.selected === true);

        if (!foundChoice && roundChoices.length) {
            const lastChoice = roundChoices[roundChoices.length - 1];

            lastChoice.selected = true;
            this.setState({
                roundChoices,
                selectedRoundId: lastChoice.value,
                hasUserSelected: true,
            });
            return;
        }

        if (isRoundChoicesEqual(prevChoices, roundChoices)) return;

        this.setState({
            roundChoices,
            selectedRoundId: (foundChoice && foundChoice.value) || null,
            hasUserSelected: false,
        });
    }

    setRound = (roundId) => {
        gtm.push('Tee Times', 'Dropdown', `${roundId} > ${window.location.href}`);
        this.setState({ selectedRoundId: roundId, hasUserSelected: true });
    }

    reverseOrder = () => {
        let sortOrder = this.state.sortOrder;

        sortOrder = sortOrder === 'ASC' ? 'DESC' : 'ASC';

        gtm.push('Tee Times', 'Select', 'Reverse Order');
        this.setState({ sortOrder });
    }

    setNextOut = (node) => {
        this.nextOutNode = node;
    }

    showNextOut = () => {
        if (this.nextOutNode) {
            const scroller = new Scroller(this.nextOutNode);
            scroller.scrollToTop();
        }
    }

    updateFavorites = () => {
        this.setState({ favoritePlayerList: favoritePlayerService.getAll() });
    }

    toggleFavourites = () => {
        gtm.push('Tee Times', 'Select', 'Favourites');
        this.setState({ showFavourites: !this.state.showFavourites });
    }

    onPredictiveSearch = (predictiveList) => {
        this.setState({ predictiveSearch: predictiveList });
    }

    sortByDate(sortOrder = 'ASC') {
        if (sortOrder === 'DESC') {
            return (a, b) => new Date(b.startDate) - new Date(a.startDate);
        }

        return (a, b) => new Date(a.startDate) - new Date(b.startDate);
    }

    getScoringRound() {
        const { scoringTeeTimes } = this.props;

        if (!scoringTeeTimes) return null;

        return getRoundById(scoringTeeTimes.rounds, this.state.selectedRoundId);
    }

    //TODO: rewrite to filters as discussed with Anton
    getRound() {
        const scoringRound = this.getScoringRound();

        if (!scoringRound || !Array.isArray(scoringRound.groups)) return null;

        let groups = [...scoringRound.groups];

        if (this.state.showFavourites) {
            groups = groups.filter((group) => {
                return group.players.some((player) => {
                    return favoritePlayerService.isFavourite(player.id);
                });
            });
        }

        if (this.state.predictiveSearch.length) {
            const predictiveIds = this.state.predictiveSearch.map((predictive) =>
                predictive.id.toString());

            groups = groups.filter((group) => {
                let hasPredictive = false;

                group.players.forEach((player) => {
                    if (predictiveIds.includes(player.id.toString())) {
                        hasPredictive = true;
                    }
                });
                return hasPredictive;
            });
        }

        groups.sort(this.sortByDate(this.state.sortOrder));

        return ({
            ...scoringRound,
            groups
        });
    }

    getNextOutDate(activeRound) {
        const activeRoundId = get(this.props, 'liveScanner.activeRoundId');

        if (!activeRoundId ||
            !nextOutAvailablityRounds.includes(activeRoundId.toString()) ||
            activeRound.id !== activeRoundId) return null;

        const nextOutGroup = activeRound.groups
            .filter((group) => group.status === groupStatus.INACTIVE)
            .sort(this.sortByDate())[0];

        return (nextOutGroup && nextOutGroup.startDate) || null;
    }

    getAllPlayers() {
        const activeRound = this.getScoringRound();
        const players = [];

        if (!activeRound) return players;

        activeRound.groups.forEach((group) => {
            group.players.forEach((player) => {
                players.push({
                    id: player.id,
                    scoring: player,
                    sitecoreData: getPlayerSitecoreData(this.props.sitecorePlayers, player.id)
                });
            });
        });

        return players;
    }

    prepareSuggestions = (players) => {
        const playersSuggestions = getSuggestionList(
            players,
            'id',
            ['sitecoreData.firstName', 'sitecoreData.lastName'],
            (item) => <PlayerSuggestion model={item.sitecoreData} />,
            this.onPredictiveSearch,
            true
        );

        const countriesSuggestions = getSuggestionList(
            players,
            'sitecoreData.country',
            ['sitecoreData.country'],
            (item) => <CountrySuggestion model={item.sitecoreData} />,
            this.onPredictiveSearch
        );

        return [...playersSuggestions, ...countriesSuggestions];
    }

    onSearchToggleState = (isActive) => {
        this.setState({ isPredictive: isActive });
    }

    getSearch(activeRound) {
        const players = this.getAllPlayers(activeRound);

        return ({
            suggestions: this.prepareSuggestions(players),
            defaultCb: getDefaultCallback(
                players,
                ['sitecoreData.firstName', 'sitecoreData.lastName'],
                this.onPredictiveSearch
            ),
            onToggleState: this.onSearchToggleState,
            isPredictive: this.state.isPredictive
        });
    }

    render() {
        const { liveScanner, sitecorePlayers, scoringTeeTimes } = this.props;

        if (liveScanner === undefined ||
            sitecorePlayers === undefined ||
            scoringTeeTimes === undefined
        ) {
            return (
                <BlockPlaceholder minHeight={300}>
                    <BlockSpinner />
                </BlockPlaceholder>
            );
        }

        const activeRound = this.getRound();
        const config = get(liveScanner, 'liveComponents.teeTimes');

        if (!config.show || !scoringTeeTimes || !activeRound) {
            const messageModel = {
                title: config.title,
                body: config.subtitle
            };

            return <LivePreMessage model={messageModel} bemList={[bemClasses.LIGHT]} />;
        }

        const nextOutDate = this.getNextOutDate(activeRound);
        const search = this.getSearch(activeRound);
        return (
            <div className="tee-times-widget">
                <TeeTimesWidgetBar
                    search={search}
                    selectedRoundId={this.state.selectedRoundId}
                    title= {this.props.options.title}
                />
                <TeeTimesWidgetList
                    sitecorePlayers={sitecorePlayers}
                    groups={activeRound.groups.slice(0, this.props.options.numberOfGames)}
                    nextOutDate={nextOutDate}
                    setNextOut={this.setNextOut}
                    selectedRoundId={this.state.selectedRoundId}
                    activeRoundId={this.props.liveScanner.activeRoundId}
                />
                <TeeTimesWidgetFooter
                    linkText={this.props.options.linkText}
                    linkUrl={this.props.options.linkUrl}
                    patronLogo={this.props.options.patronLogo}
                />
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    liveScanner: state.scoring[endpointNames.LIVE_SCANNER],
    sitecorePlayers: state.scoring[endpointNames.PLAYERS],
    scoringTeeTimes: state.scoring[endpointNames.TEE_TIMES],
});

const TeeTimesLayoutContainer = connect(
    mapStateToProps,
    null
)(TeeTimesWidgetLayout);

export default TeeTimesLayoutContainer;
